/**
 * SessionRecoveryService — the ONE place mid-session token expiry is handled.
 *
 * GqlClient stays dependency-light: it refreshes once then throws UNAUTHORIZED
 * (its `doRefresh` uses a raw fetch, outside the retry path, so refresh 401s
 * never recurse). When that thrown UNAUTHORIZED surfaces — via the global
 * ErrorHandler (uncaught) or a page's catch — it lands here.
 *
 * `handleExpiry()` is single-flight guarded: concurrent 401s trigger ONE toast
 * + ONE redirect, never a loop. The first-load case is already covered by the
 * auth guard; this covers expiry AFTER the app is running.
 *
 * Demo mode never reaches this (PLATFORM_MODE off → no auth, no GqlClient).
 */

import { Injectable, inject } from '@angular/core';
import { PaperToastService } from '@ricoh-paper/angular';

import { VisitorsAuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class SessionRecoveryService {
  private readonly auth = inject(VisitorsAuthService);
  private readonly toasts = inject(PaperToastService);

  private recovering = false;

  /** Notify the user their session expired, then redirect back to login. */
  handleExpiry(): void {
    if (this.recovering) return; // single-flight — one redirect, not a loop
    this.recovering = true;

    try {
      this.toasts.show({
        id: 'session-expired',
        title: $localize`:@@visitors-session-expired-title:Your session expired`,
        body: $localize`:@@visitors-session-expired-body:Signing you back in…`,
        severity: 'warning',
        priority: 'high',
      });
    } catch {
      /* toast stack unavailable — redirect still happens below */
    }

    // Return to the current view after sign-in. A hard redirect, so the
    // single-flight flag never needs resetting (the page is leaving).
    this.auth.loginRedirect();
  }
}
